import { useState, useCallback, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { Upload, X, Loader2, AlertCircle, Image as ImageIcon } from "lucide-react"
import { imageApi } from "@/shared/api/imageApi"
import { queryKeys } from "@/shared/api/queryClient"

const MAX_FILES = 5
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

interface UploadingFile {
  id: string
  file: File
  progress: number
  error?: string
}

interface ImageUploadFieldProps {
  value: string[] // Array of imageIds
  onChange: (imageIds: string[]) => void
  maxFiles?: number
  maxFileSize?: number
  disabled?: boolean
  className?: string
}

/**
 * Image upload field with drag-drop, progress, and preview.
 * Uploads immediately on selection and manages array of imageIds.
 */
export function ImageUploadField({
  value = [],
  onChange,
  maxFiles = MAX_FILES,
  maxFileSize = MAX_FILE_SIZE,
  disabled = false,
  className = "",
}: ImageUploadFieldProps) {
  const [uploading, setUploading] = useState<UploadingFile[]>([])
  const [isDragging, setIsDragging] = useState(false)

  // Fetch signed URLs for existing images
  const { data: signedUrlsResponse } = useQuery({
    queryKey: queryKeys.images.signedUrls(value),
    queryFn: () => imageApi.getSignedUrls(value),
    enabled: value.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Derive preview images from query data
  const previewImages = useMemo(
    () => signedUrlsResponse?.images ?? [],
    [signedUrlsResponse]
  )

  const remainingSlots = maxFiles - value.length - uploading.length

  const validateFile = useCallback(
    (file: File): string | null => {
      if (!file.type.startsWith("image/")) {
        return "Only image files are allowed"
      }
      if (file.size > maxFileSize) {
        return `File too large (max ${maxFileSize / 1024 / 1024}MB)`
      }
      return null
    },
    [maxFileSize]
  )

  const uploadFile = async (file: File): Promise<string | null> => {
    const tempId = `temp-${Date.now()}-${Math.random()}`

    setUploading((prev) => [...prev, { id: tempId, file, progress: 0 }])

    try {
      // Simulate progress (actual upload doesn't provide progress)
      setUploading((prev) =>
        prev.map((u) => (u.id === tempId ? { ...u, progress: 30 } : u))
      )

      const imageId = await imageApi.uploadFile(file)

      setUploading((prev) =>
        prev.map((u) => (u.id === tempId ? { ...u, progress: 100 } : u))
      )

      // Remove from uploading after brief delay to show 100%
      setTimeout(() => {
        setUploading((prev) => prev.filter((u) => u.id !== tempId))
      }, 300)

      return imageId
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Upload failed"
      setUploading((prev) =>
        prev.map((u) => (u.id === tempId ? { ...u, error: errorMessage, progress: 0 } : u))
      )
      return null
    }
  }

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files).slice(0, remainingSlots)
      const validFiles: File[] = []
      const errors: string[] = []

      for (const file of fileArray) {
        const error = validateFile(file)
        if (error) {
          errors.push(`${file.name}: ${error}`)
        } else {
          validFiles.push(file)
        }
      }

      if (errors.length > 0) {
        // For simplicity, show first error in console; could enhance with toast
        console.error("File validation errors:", errors)
      }

      if (validFiles.length === 0) return

      const newImageIds: string[] = []
      for (const file of validFiles) {
        const imageId = await uploadFile(file)
        if (imageId) {
          newImageIds.push(imageId)
        }
      }

      if (newImageIds.length > 0) {
        onChange([...value, ...newImageIds])
      }
    },
    [onChange, value, remainingSlots, validateFile]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) setIsDragging(true)
  }, [disabled])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      if (disabled || remainingSlots <= 0) return
      handleFiles(e.dataTransfer.files)
    },
    [disabled, remainingSlots, handleFiles]
  )

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files)
      }
      // Reset input so same file can be selected again
      e.target.value = ""
    },
    [handleFiles]
  )

  const removeImage = useCallback(
    (imageId: string) => {
      onChange(value.filter((id) => id !== imageId))
    },
    [onChange, value]
  )

  const removeUpload = useCallback((tempId: string) => {
    setUploading((prev) => prev.filter((u) => u.id !== tempId))
  }, [])

  const canAddMore = remainingSlots > 0 && !disabled

  return (
    <div className={className}>
      {/* Drop Zone */}
      {canAddMore && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
            ${isDragging
              ? "border-emerald-400 bg-emerald-50/50"
              : "border-slate-200/80 hover:border-slate-300 bg-slate-50/30"
            }
            ${disabled ? "opacity-50 cursor-not-allowed" : ""}
          `}
        >
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileInput}
            disabled={disabled}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragging ? "text-emerald-500" : "text-gray-400"}`} />
          <p className="text-sm text-gray-600">
            <span className="font-medium text-emerald-600">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-gray-400 mt-1">
            PNG, JPG, GIF up to {maxFileSize / 1024 / 1024}MB ({remainingSlots} remaining)
          </p>
        </div>
      )}

      {/* Uploading Files */}
      {uploading.length > 0 && (
        <div className="mt-3 space-y-2">
          {uploading.map((upload) => (
            <div
              key={upload.id}
              className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg border border-slate-200/50"
            >
              <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center flex-shrink-0">
                {upload.error ? (
                  <AlertCircle className="w-5 h-5 text-red-500" />
                ) : (
                  <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-700 truncate">{upload.file.name}</p>
                {upload.error ? (
                  <p className="text-xs text-red-500">{upload.error}</p>
                ) : (
                  <div className="h-1.5 bg-slate-200 rounded-full mt-1 overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                      style={{ width: `${upload.progress}%` }}
                    />
                  </div>
                )}
              </div>
              {upload.error && (
                <button
                  type="button"
                  onClick={() => removeUpload(upload.id)}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Image Previews */}
      {previewImages.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {previewImages.map((image) => (
            <div
              key={image.imageId}
              className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200/50 bg-slate-100"
            >
              <img
                src={image.signedUrl}
                alt={image.fileName}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeImage(image.imageId)}
                disabled={disabled}
                className="absolute top-1 right-1 p-1 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Empty state when no images and can't add more */}
      {value.length === 0 && uploading.length === 0 && !canAddMore && (
        <div className="flex items-center justify-center p-6 bg-slate-50/50 rounded-lg border border-slate-200/50">
          <div className="text-center">
            <ImageIcon className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-400">No images</p>
          </div>
        </div>
      )}
    </div>
  )
}
